"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";

import { useLanguage } from "../i18n/LanguageProvider";

/* ============================================================
   HERO SLIDER - FULL BLEED

   TINGGI
   `min(56.25vw, 100svh - tinggi header)`:
   - Layar sempit  : 56.25vw menang, poster tampil 16:9 persis.
   - Layar lebar   : batas tinggi menang, poster terpotong sedikit
                     di atas dan bawah.

   Angka header diambil dari Header.jsx versi terbaru:
     mobile  : 64px  (h-16 main bar, utility bar disembunyikan)
     desktop : 112px (h-10 utility bar + h-[72px] main bar)

   Dipakai svh, bukan vh. Di browser mobile, vh mengabaikan bilah alamat
   yang muncul-hilang sehingga hero sering terpotong di bawah.

   OBJECT-COVER, BUKAN OBJECT-CONTAIN
   Full-bleed berarti gambar harus mentok ke tepi kiri-kanan.
   object-contain akan menyusutkan poster dan memunculkan pita plum di
   samping, yang justru membatalkan efek full-bleed.

   Konsekuensinya: sebagian atas dan bawah poster terpotong di layar
   lebar. Saat mendesain poster berikutnya, JANGAN taruh teks penting
   di 10% teratas atau terbawah.

   ============================================================
   >>> KENDALI SLIDER SENGAJA DIBUAT NYARIS TAK TERLIHAT <<<

   Titik indikator DIHAPUS, dan panah kiri-kanan disamarkan sampai
   kursor mendekat. Poster hero diperlakukan sebagai papan pengumuman
   berputar, bukan alat navigasi - kendali yang mencolok menutupi
   bagian poster yang justru ingin dibaca.

   HARGA YANG DIBAYAR, SUPAYA DISADARI:
   Di layar sentuh tidak ada hover, jadi panah akan selamanya berada
   pada kondisi samarnya. Pengunjung ponsel praktis tidak punya isyarat
   bahwa hero ini berisi tiga slide - mereka hanya melihat gambar
   berganti sendiri tiap enam detik. Autoplay tetap jalan, jadi seluruh
   poster tetap terlihat; yang hilang adalah kendalinya.

   KALAU SUATU SAAT INGIN DIBALIK: pindahkan kelas samar (bg-black/10,
   text-white/40) ke varian md: pada KENDALI di bawah, dan biarkan
   nilai dasarnya tetap pekat. Panah jadi jelas di ponsel, tetap samar
   di desktop. Satu baris, tidak perlu menyentuh yang lain.
   ============================================================ */

const HERO_HEIGHT =
  "h-[min(56.25vw,calc(100svh-64px))] md:h-[min(56.25vw,calc(100svh-112px))]";

/* Warna area kosong kalau ada slide yang rasionya menyimpang. */
const LETTERBOX = "bg-[var(--c-brand)]";

const AUTOPLAY_MS = 2000;

/* Kelas bersama kedua panah. Ditulis sebagai konstanta supaya
   keduanya tidak bisa berbeda diam-diam saat salah satu diubah.

   Kondisi diam : bg-black/10 + text-white/40 - ada, tapi tidak
                  merebut perhatian dari poster.
   Hover/fokus  : kembali pekat, supaya begitu dicari langsung
                  terbaca. focus-visible ikut dipekatkan - tanpa itu
                  pengguna keyboard mendapat panah samar yang sedang
                  dia fokuskan.

   Ditulis satu baris - alasan CRLF yang sama seperti di berkas lain. */
const KENDALI =
  "absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/10 p-2 text-white/40 backdrop-blur-[2px] transition duration-300 hover:bg-black/45 hover:text-white focus-visible:bg-black/45 focus-visible:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white motion-reduce:transition-none md:p-3";

/* alt WAJIB memuat isi teks posternya. Untuk gambar yang isinya teks,
   alt adalah satu-satunya cara screen reader dan mesin pencari
   mengakses informasi itu. Ganti dengan isi poster yang sebenarnya. */
const SLIDES = [
  { src: "/images/ke1.png", mobileSrc: null, altKey: "hero.alt1" },
  { src: "/images/ke3.png", mobileSrc: null, altKey: "hero.alt2" },
  { src: "/images/ke2..jpg", mobileSrc: null, altKey: "hero.alt3" },
];

/* ⚠️ POSTERNYA SENDIRI TIDAK IKUT BERGANTI BAHASA.

   Yang diterjemahkan di sini hanya `alt` - teks yang dibaca pembaca
   layar dan mesin pencari. Gambarnya tetap banner1.png yang berisi
   tulisan Bahasa Indonesia.

   Jadi pengunjung English akan melihat poster Indonesia dengan
   keterangan Inggris. Kalau versi Inggris memang serius dikerjakan,
   posternya perlu dibuat dua versi (misalnya banner1-en.png) dan slide
   dipilih berdasarkan `lang`. Itu pekerjaan desain, bukan kode, jadi
   saya tidak menebaknya di sini.
*/


export default function HeroSlider() {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const total = SLIDES.length;

  const go = useCallback(
    (next) => setIndex(((next % total) + total) % total),
    [total]
  );

  /* Autoplay berhenti saat pointer di atas slider, saat ada elemen
     yang fokus (keyboard), dan saat tab tidak aktif.

     Autoplay ini SATU-SATUNYA cara pengunjung ponsel melihat slide
     kedua dan ketiga sekarang, karena panahnya samar dan titiknya
     sudah tidak ada. Jangan dimatikan tanpa mengganti kendalinya
     dengan sesuatu yang lain. */
  useEffect(() => {
    if (paused || total < 2) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduce.matches) return;

    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, AUTOPLAY_MS);

    return () => clearInterval(timer.current);
  }, [paused, total]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") go(index - 1);
    if (e.key === "ArrowRight") go(index + 1);
  };

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={t("hero.wilayahAria")}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`relative w-full overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white ${LETTERBOX} ${HERO_HEIGHT}`}
    >
      {SLIDES.map((slide, i) => {
        const active = i === index;

        return (
          <div
            key={slide.src}
            aria-hidden={!active}
            className={`absolute inset-0 transition-opacity duration-700 motion-reduce:transition-none ${
              active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            {/* Dua sumber gambar: mobileSrc dipakai kalau ada.
                Keduanya object-cover - lihat penjelasan di atas. */}
            {slide.mobileSrc ? (
              <>
                <Image
                  src={slide.mobileSrc}
                  alt={t(slide.altKey)}
                  fill
                  sizes="100vw"
                  priority={i === 0}
                  className="object-cover object-center md:hidden"
                />
                {/* alt dikosongkan di versi desktop supaya pembaca layar
                    tidak membacakan teks yang sama dua kali - keduanya
                    ada di DOM, hanya satu yang tampil. */}
                <Image
                  src={slide.src}
                  alt=""
                  fill
                  sizes="100vw"
                  priority={i === 0}
                  className="hidden object-cover object-center md:block"
                />
              </>
            ) : (
              <Image
                src={slide.src}
                alt={t(slide.altKey)}
                fill
                sizes="100vw"
                priority={i === 0}
                className="object-cover object-center"
              />
            )}
          </div>
        );
      })}

      {/* ---------- PANAH ----------
         top-1/2, bukan top-[42%]. Angka 42% dulu dipakai untuk
         mengimbangi barisan titik di bawah; titiknya sudah tidak ada,
         jadi tengah adalah tengah. */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label={t("hero.sebelumnya")}
            className={`${KENDALI} left-3 md:left-5`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label={t("hero.berikutnya")}
            className={`${KENDALI} right-3 md:right-5`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
  